import React, { useEffect, useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import useSocketStore from '../../../../store/useSocketStore';
import CallDialog from './CallDialog';
import IncomingCall from './IncomingCall';
import './ChatApp.css';

const dummyResidents = [
  { id: '1', initials: 'YP', name: 'Yash Pawar', role: 'Admin', phone: '8850552415' },
  { id: '2', initials: 'AC', name: 'Ayush Chourasiya', role: 'Secretary', phone: '8097069559' },
  { id: '3', initials: 'VW', name: 'Vaishnaviii Waghule', role: 'Resident', phone: '9637837447' },
  { id: '4', initials: 'AK', name: 'Amisha Khirari', role: 'Resident', phone: '9307672709' },
  { id: '5', initials: 'KR', name: 'Krupalsing Rajput', role: 'Resident', phone: '8208483111' },
  { id: '6', initials: 'MM', name: 'Mohan Mitkari', role: 'Resident', phone: '9673162162' }
];

const CHAT_API_URL = process.env.REACT_APP_CHAT_API_URL || 'http://localhost:5173/api';

const renderAvatar = (name, url, size = '36px') => {
  if (url) {
    return <img src={url} alt={name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
  }
  return <>{name ? name.charAt(0).toUpperCase() : 'U'}</>;
};

const ChatApp = () => {
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const { socket, connectSocket, disconnectSocket } = useSocketStore();
  
  const [groups, setGroups] = useState([]);
  const [activeGroup, setActiveGroup] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  
  const [searchQuery, setSearchQuery] = useState('');
  const [modalSearchQuery, setModalSearchQuery] = useState('');
  const [showNewChatModal, setShowNewChatModal] = useState(false);
  const [showNewGroupModal, setShowNewGroupModal] = useState(false);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  
  const [newGroupName, setNewGroupName] = useState('');
  const [isGroupCallAllowed, setIsGroupCallAllowed] = useState(false);
  const [showAddMemberModal, setShowAddMemberModal] = useState(false);
  const [addMemberSearchQuery, setAddMemberSearchQuery] = useState('');
  const [selectedMembersToAdd, setSelectedMembersToAdd] = useState([]);
  const [showConfirmModal, setShowConfirmModal] = useState({ visible: false, type: '', groupId: null, groupName: '' });

  const [callConfig, setCallConfig] = useState(null);
  const [incomingCallData, setIncomingCallData] = useState(null);
  const [callAnswered, setCallAnswered] = useState(false);
  const [activeGroupMenuId, setActiveGroupMenuId] = useState(null);
  const [usersList, setUsersList] = useState([]);

  const messagesEndRef = useRef(null);
  const callStartTimeRef = useRef(null);
  const callConfigRef = useRef(null);
  const callAnsweredRef = useRef(false);

  useEffect(() => { callConfigRef.current = callConfig; }, [callConfig]);

  useEffect(() => {
    if (user?.id) {
        connectSocket(user.id);
        fetchGroups();
        fetchUsers();
    }
    return () => disconnectSocket();
  }, [user?.id, token]);

  useEffect(() => {
    if (socket) {
      socket.on('receive-message', (msg) => {
        if (activeGroup && msg.group === activeGroup._id) {
          setMessages(prev => {
            if (prev.find(m => m._id === msg._id)) return prev;
            return [...prev, msg];
          });
        } else {
          setGroups(prevGroups => {
            const exists = prevGroups.find(g => g._id === msg.group);
            if (!exists) {
              fetchGroups();
              return prevGroups;
            }
            return prevGroups.map(g => {
              if (g._id === msg.group) {
                return {
                  ...g,
                  lastMessage: {
                    text: msg.content || msg.text || '',
                    timestamp: msg.createdAt || new Date().toISOString(),
                    senderName: msg.sender?.name || 'Unknown'
                  }
                };
              }
              return g;
            });
          });
        }
      });
      socket.on('incoming-call', (data) => setIncomingCallData(data));
      socket.on('call-ring-cancelled', () => setIncomingCallData(null));
      socket.on('call-answered', () => { setCallAnswered(true); callAnsweredRef.current = true; });
      socket.on('call-ring-declined', () => {
        const config = callConfigRef.current;
        setCallConfig(null);
        if (config?.initiator) {
           sendSystemMessage(activeGroup?._id, `📞 ${config.type === 'video' ? 'Video' : 'Audio'} Call declined`);
        }
      });
      return () => {
        socket.off('receive-message');
        socket.off('incoming-call');
        socket.off('call-ring-cancelled');
        socket.off('call-answered');
        socket.off('call-ring-declined');
      };
    }
  }, [socket, activeGroup]);

  useEffect(() => {
    if (activeGroup) {
      fetchMessages(activeGroup._id);
      if (socket) socket.emit('join-group', activeGroup._id);
    }
  }, [activeGroup]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      const target = e.target;
      if (!target || typeof target.closest !== 'function') return;
      
      if (!target.closest('.btn-group-menu') && !target.closest('.group-dropdown-menu')) {
        setActiveGroupMenuId(null);
      }
      if (!target.closest('.btn-header-actions') && !target.closest('.header-dropdown-menu')) {
        setActiveGroupActionsId(null);
      }
      if (!target.closest('.btn-emoji-picker') && !target.closest('.emoji-picker-menu')) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, []);

  const fetchGroups = async () => {
    try {
      const res = await fetch(`${CHAT_API_URL}/user/groups`, { headers: { 'Authorization': `Bearer ${token}` } });
      const apiResponse = await res.json();
      
      const groupsArray = apiResponse.data || apiResponse;
      if (!Array.isArray(groupsArray)) {
        console.warn('Expected array for chat groups, got:', groupsArray);
        setGroups([]);
        return;
      }
      
      const transformedGroups = groupsArray.map(group => {
        const chatGroup = {
          ...group,
          _id: group._id || group.chatGroupId || group.id || group.groupId || '',
          name: group.isDirectMessage ? '' : (group.name || 'Unnamed Group'),
          isDirectMessage: group.isDirectMessage || false,
          unreadCount: group.unreadCount || 0,
          members: group.members || [],
          createdAt: group.createdAt || null,
        };
        
        if (group.lastMessage) {
          chatGroup.lastMessage = {
            text: group.lastMessage.messageText || group.lastMessage.text || '',
            timestamp: group.lastMessage.timestamp || group.lastMessage.createdAt,
            senderName: group.lastMessage.senderName || group.lastMessage.sender || 'Unknown'
          };
        }
        return chatGroup;
      }).filter(g => g._id);
      
      setGroups(transformedGroups);
      if (activeGroup) {
        const updatedActive = transformedGroups.find(g => g._id === activeGroup._id);
        if (updatedActive) setActiveGroup(updatedActive);
      }
      if (socket) {
        transformedGroups.forEach(g => socket.emit('join-group', g._id));
      }
    } catch (err) { console.error(err); }
  };

  const fetchUsers = async () => {
    try {
      const res = await fetch(`${CHAT_API_URL}/admin/users`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setUsersList(data.data || data);
    } catch (err) { console.error(err); }
  };

  const fetchMessages = async (groupId) => {
    try {
      const res = await fetch(`${CHAT_API_URL}/user/groups/${groupId}/messages`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await res.json();
      setMessages(data);
    } catch (err) { console.error(err); }
  };

  const sendSystemMessage = async (groupId, content) => {
    try {
      await fetch(`${CHAT_API_URL}/user/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ groupId, content, isSystemMessage: true })
      });
    } catch (err) { console.error(err); }
  };

  const clearChat = async (e, groupId) => {
    e.stopPropagation();
    setActiveGroupMenuId(null);
    try {
      const res = await fetch(`${CHAT_API_URL}/user/groups/${groupId}/clear-chat`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        if (activeGroup?._id === groupId) setMessages([]);
        alert('Chat cleared successfully.');
      }
    } catch (err) { console.error(err); }
  };

  const toggleMuteNotifications = async (e, group) => {
    e.stopPropagation();
    setActiveGroupMenuId(null);
    const newMutedState = !group.isMutedByMe;
    
    setGroups(prev => prev.map(g => g._id === group._id ? {...g, isMutedByMe: newMutedState} : g));
    if (activeGroup?._id === group._id) {
      setActiveGroup(prev => ({...prev, isMutedByMe: newMutedState}));
    }

    try {
      const res = await fetch(`${CHAT_API_URL}/user/groups/${group._id}/mute-notifications`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ isMuted: newMutedState })
      });
      if (!res.ok) {
        setGroups(prev => prev.map(g => g._id === group._id ? {...g, isMutedByMe: !newMutedState} : g));
        if (activeGroup?._id === group._id) {
          setActiveGroup(prev => ({...prev, isMutedByMe: !newMutedState}));
        }
        alert('Failed to toggle mute.');
      } else {
        // Send a system message in chat
        sendSystemMessage(group._id, newMutedState ? 'You muted the chat' : 'You unmuted the chat');
      }
    } catch (err) {
      console.error(err);
      setGroups(prev => prev.map(g => g._id === group._id ? {...g, isMutedByMe: !newMutedState} : g));
      if (activeGroup?._id === group._id) {
        setActiveGroup(prev => ({...prev, isMutedByMe: !newMutedState}));
      }
    }
  };

  const deleteGroup = async (e, groupId) => {
    if (e) e.stopPropagation();
    setActiveGroupMenuId(null);
    try {
      const res = await fetch(`${CHAT_API_URL}/admin/groups/${groupId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        if (activeGroup?._id === groupId) setActiveGroup(null);
        fetchGroups();
        alert('Group deleted successfully.');
      }
    } catch (err) { console.error(err); }
  };

  const leaveGroup = async (e, groupId) => {
    e.stopPropagation();
    setActiveGroupMenuId(null);
    try {
      const res = await fetch(`${CHAT_API_URL}/user/groups/${groupId}/leave`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        if (activeGroup?._id === groupId) setActiveGroup(null);
        fetchGroups();
        alert('You left the group successfully.');
      }
    } catch (err) { console.error(err); }
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !activeGroup) return;
    try {
      const res = await fetch(`${CHAT_API_URL}/user/messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ groupId: activeGroup._id, content: newMessage })
      });
      if (res.ok) setNewMessage('');
    } catch (err) { console.error(err); }
  };

  const startDirectMessage = async (targetUserId) => {
    setShowNewChatModal(false);
    if (targetUserId === user.id) return;
    try {
      const res = await fetch(`${CHAT_API_URL}/user/direct-message`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ targetUserId })
      });
      const data = await res.json();
      const groupData = data.data || data;
      if (res.ok) {
        fetchGroups();
        setActiveGroup(groups.find(g => g._id === groupData._id) || groupData);
        setShowNewChatModal(false);
      }
    } catch(err) { console.error(err); }
  };

  const createGroup = async () => {
    if (!newGroupName.trim()) return;
    try {
      const res = await fetch(`${CHAT_API_URL}/admin/groups`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newGroupName, members: [], isAudioCallAllowed: isGroupCallAllowed })
      });
      if (res.ok) {
        fetchGroups();
        setNewGroupName('');
        setIsGroupCallAllowed(false);
      }
    } catch (err) { console.error(err); }
  };

  const addGroupMember = async (groupId, membersArray) => {
    if (!membersArray || membersArray.length === 0) return;
    try {
      const res = await fetch(`${CHAT_API_URL}/admin/groups/${groupId}/add-members`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ members: Array.isArray(membersArray) ? membersArray : [membersArray] })
      });
      if (res.ok) {
        fetchGroups();
      }
    } catch (err) { console.error(err); }
  };

  const removeGroupMember = async (groupId, phoneNumber) => {
    try {
      const res = await fetch(`${CHAT_API_URL}/admin/groups/${groupId}/remove-member`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ phoneNumber })
      });
      if (res.ok) {
        fetchGroups();
        alert('Member removed successfully.');
      }
    } catch (err) { console.error(err); }
  };

  const updateCallPermissions = async (groupId, isAudioCallAllowed) => {
    try {
      const res = await fetch(`${CHAT_API_URL}/admin/groups/${groupId}/call-permissions`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ isAudioCallAllowed, isVideoCallAllowed: true })
      });
      if (res.ok) {
        fetchGroups();
      }
    } catch (err) { console.error(err); }
  };

  const [activeGroupActionsId, setActiveGroupActionsId] = useState(null);
  
  const deleteGroupById = async (groupId) => {
    try {
      const res = await fetch(`${CHAT_API_URL}/user/groups/${groupId}/delete`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (res.ok) {
        setGroups(groups.filter(g => g._id !== groupId));
        if (activeGroup?._id === groupId) setActiveGroup(null);
      }
    } catch (e) { console.error(e); }
  };

  const initiateCall = (type) => {
    setCallConfig({ active: true, type, initiator: true, groupId: activeGroup._id });
    callStartTimeRef.current = Date.now();
    setCallAnswered(false);
    callAnsweredRef.current = false;
    if (socket && activeGroup) {
      socket.emit('start-call-ring', {
        groupId: activeGroup._id,
        callerName: user.name === 'User' ? user.phoneNumber : (user.name || user.phoneNumber),
        isVideo: type === 'video'
      });
    }
  };

  const handleEndCall = () => {
    const config = callConfigRef.current;
    if (config?.initiator) {
       if (callAnsweredRef.current) {
          const duration = Math.floor((Date.now() - callStartTimeRef.current) / 1000);
          sendSystemMessage(activeGroup._id, `📞 Call ended (${duration} sec)`);
       } else {
          sendSystemMessage(activeGroup._id, `📞 Missed Call`);
       }
    }
    setCallConfig(null);
    if (socket && activeGroup) socket.emit('cancel-call-ring', { groupId: activeGroup._id });
  };

  const generateColorFromString = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      hash = str.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      '#667eea', '#764ba2', '#f093fb', '#f5576c',
      '#4facfe', '#43e97b', '#fa709a', '#30cfd0',
      '#f6d365', '#fda085', '#96fbc4', '#f9d423'
    ];
    return colors[Math.abs(hash) % colors.length];
  };

  const renderAvatar = (name, imgUrl, id, fontSize = '18px') => {
    if (imgUrl) return <img src={imgUrl} alt="avatar" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />;
    const initial = name?.substring(0, 1).toUpperCase() || 'U';
    return <span style={{ color: '#0D1A3A', width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e4ec', fontSize, fontWeight: '600' }}>{initial}</span>;
  };

  const getCurrentUserIdentifiers = () => {
    return new Set(
      [
        user?.id,
        user?.phoneNumber,
        user?.email,
        user?.employeeCode
      ]
        .filter(Boolean)
        .map(id => id.toString().trim().toLowerCase())
    );
  };

  const isCurrentChatUser = (member) => {
    if (!member) return false;
    if (typeof member === 'string' || typeof member === 'number') {
      return getCurrentUserIdentifiers().has(member.toString().trim().toLowerCase());
    }
    const memberName = (member.fullName || member.name || '').trim().toLowerCase();
    const currentName = (user?.name || user?.fullName || '').trim().toLowerCase();
    if (memberName && currentName && currentName !== 'you' && memberName === currentName) {
      return true;
    }
    const memberIds = [
      member._id, member.id, member.userId, member.phoneNumber,
      member.mobileNumber, member.phone, member.employeeCode, member.userName, member.email
    ].filter(Boolean).map(id => id.toString().trim().toLowerCase());
    return memberIds.some(id => getCurrentUserIdentifiers().has(id));
  };

  const getOtherDirectMessageMember = (members) => {
    const others = members.filter(m => !isCurrentChatUser(m));
    if (others.length === 1) return others[0];
    if (others.length > 1 && (user?.name || user?.fullName)) {
      const byName = others.find(m => {
        const name = (m.fullName || m.name || '').trim().toLowerCase();
        return name && name !== (user.name || user.fullName).trim().toLowerCase();
      });
      if (byName) return byName;
    }
    return others[0] || null;
  };

  const getDirectMessagePartner = (group) => {
    if (group?.dmPartnerName && !isCurrentChatUser({ name: group.dmPartnerName })) {
      return { name: group.dmPartnerName };
    }
    const members = group?.members || [];
    if (members.length > 0) {
      const otherByIdentity = getOtherDirectMessageMember(members);
      if (otherByIdentity) return otherByIdentity;
    }
    const apiPartner = group?.otherUser || group?.recipient || group?.partner || group?.dmUser || group?.targetUser || group?.chatWith;
    if (apiPartner && !isCurrentChatUser(apiPartner)) return apiPartner;
    
    const lastSender = group?.lastMessage?.senderName || group?.lastMessage?.sender?.name || (typeof group?.lastMessage?.sender === 'string' ? group.lastMessage.sender : null);
    if (lastSender && !isCurrentChatUser({ name: lastSender })) {
      return { name: lastSender };
    }
    if (group?.name && !isCurrentChatUser({ name: group.name })) {
      return { name: group.name };
    }
    return null;
  };

  const getGroupName = (g) => {
    if (!g) return '';
    if (g.isDirectMessage) {
      const partner = getDirectMessagePartner(g);
      if (partner) {
        const displayName = partner.fullName || partner.name;
        if (displayName && displayName !== 'User') return displayName;
        return partner.phoneNumber || partner.userName || partner.email || 'Direct Message';
      }
      return 'Direct Message';
    }
    return g.name || 'Unnamed Group';
  };

  const getGroupAvatarUrl = (g) => {
    if (!g) return null;
    if (g.isDirectMessage) {
      const partner = getDirectMessagePartner(g);
      if (partner) return partner.userImage || partner.avatarUrl || partner.avatar || null;
    }
    return g.avatarUrl || g.avatar || null;
  };

  const sortedGroups = [...groups].sort((a, b) => {
    const timeA = a.lastMessage?.timestamp ? new Date(a.lastMessage.timestamp).getTime() : new Date(a.createdAt || 0).getTime();
    const timeB = b.lastMessage?.timestamp ? new Date(b.lastMessage.timestamp).getTime() : new Date(b.createdAt || 0).getTime();
    return timeB - timeA;
  });

  const filteredGroups = sortedGroups.filter(g => getGroupName(g).toLowerCase().includes(searchQuery.toLowerCase()));

  const handleEmojiSelect = (emoji) => {
    setNewMessage(prev => prev + emoji);
    setShowEmojiPicker(false);
  };

  const emojiList = ['😀', '😃', '😄', '😁', '😆', '😅', '😂', '🤣', '😊', '😇', '🙂', '🙃', '😉', '😌', '😍', '🥰', '😘', '😗', '😙', '😚', '😋', '😛', '😝', '😜', '🤪', '🤨', '🧐', '🤓', '😎', '🤩', '🥳', '😏', '😒', '😞', '😔', '😟', '😕', '🙁', '☹️', '😣', '😖', '😫', '😩', '🥺', '😢', '😭', '😤', '😠', '😡', '🤬', '🤯', '😳', '🥵', '🥶', '😱', '😨', '😰', '😥', '😓', '🤗', '🤔', '🤭', '🤫', '🤥', '😶', '😐', '😑', '😬', '🙄', '😯', '😦', '😧', '😮', '😲', '🥱', '😴', '🤤', '😪', '😵', '🤐', '🥴', '🤢', '🤮', '🤧', '😷', '🤒', '🤕', '🤑', '🤠', '😈', '👿', '👹', '👺', '🤡', '💩', '👻', '💀', '☠️', '👽', '👾', '🤖', '🎃', '😺', '😸', '😹', '😻', '😼', '😽', '🙀', '😿', '😾'];

  const [showDirectMessages, setShowDirectMessages] = useState(true);
  const [showGroupChats, setShowGroupChats] = useState(true);

  const directMessagesList = filteredGroups.filter(g => g.isDirectMessage);
  const groupChatsList = filteredGroups.filter(g => !g.isDirectMessage);

  const [showAvatarModal, setShowAvatarModal] = useState(false);

  return (
    <div className="bg-white border flex flex-col rounded-lg flex-1 h-full w-full overflow-hidden" style={{ minHeight: '80vh', maxHeight: '85vh' }}>
      <div className="chat-container">
        <div className="chat-layout">
        
        {/* Left Sidebar */}
        <div className="chats-sidebar">
          <div className="sidebar-header">
            <h3 style={{ margin: '20px 0 15px 10px', fontSize: '18px', fontWeight: 'bold', color: '#0D1A3A' }}>Contacts &amp; Groups</h3>
            <div className="search-container" style={{ position: 'relative', marginBottom: '15px' }}>
              <span style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', display: 'flex', alignItems: 'center', color: '#888' }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <circle cx="11" cy="11" r="8"></circle>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
                </svg>
              </span>
              <input 
                type="text" 
                className="search-input" 
                style={{ width: '100%', padding: '8px 10px 8px 30px', border: '1px solid #e0e0e0', borderRadius: '8px', fontSize: '13px', outline: 'none' }}
                placeholder="Search chats or groups..." 
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
              />
            </div>
            
            <div className="action-buttons-row">
              <button className="btn-action" onClick={() => setShowNewChatModal(true)}>New Chat</button>
              <button className="btn-action" onClick={() => setShowNewGroupModal(true)}>New Group</button>
            </div>
          </div>
          
          <div className="groups-list" style={{ marginTop: '10px', overflowY: 'auto', flex: 1 }}>
            
            <div className="list-section">
              <h4 className="section-title" onClick={() => setShowDirectMessages(!showDirectMessages)} style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none', margin: '0 0 10px 0', padding: '0 20px', fontSize: '14px', color: '#0D1A3A' }}>
                <svg style={{ transform: showDirectMessages ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
                Direct Messages
              </h4>
              {showDirectMessages && directMessagesList.map(g => (
                <div key={g._id} className={`group-item ${activeGroup?._id === g._id ? 'active' : ''}`} onClick={() => setActiveGroup(g)}>
                  <div className="group-avatar" style={{ overflow: 'hidden' }}>
                    {renderAvatar(getGroupName(g), getGroupAvatarUrl(g), g._id)}
                  </div>
                  <div className="group-info">
                    <div className="group-info-header">
                      <h4 className="group-name">{getGroupName(g)}</h4>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button className="btn-group-menu" onClick={(e) => { e.stopPropagation(); setActiveGroupMenuId(activeGroupMenuId === g._id ? null : g._id); }}>⋮</button>
                    {activeGroupMenuId === g._id && (
                      <div className="group-dropdown-menu" style={{ position: 'absolute', right: 0, top: '25px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 10, width: '120px' }}>
                        <button style={{ width: '100%', padding: '8px 10px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }} onClick={(e) => clearChat(e, g._id)}>Clear chat</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            <div className="list-section" style={{ marginTop: '10px' }}>
              <h4 className="section-title" onClick={() => setShowGroupChats(!showGroupChats)} style={{ marginTop: '10px', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '6px', userSelect: 'none', margin: '10px 0 10px 0', padding: '0 20px', fontSize: '14px', color: '#0D1A3A' }}>
                <svg style={{ transform: showGroupChats ? 'rotate(0deg)' : 'rotate(-90deg)', transition: 'transform 0.2s' }} width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
                Groups
              </h4>
              {showGroupChats && groupChatsList.map(g => (
                <div key={g._id} className={`group-item ${activeGroup?._id === g._id ? 'active' : ''}`} onClick={() => setActiveGroup(g)}>
                  <div className="group-avatar" style={{ overflow: 'hidden' }}>
                    {renderAvatar(getGroupName(g), getGroupAvatarUrl(g), g._id)}
                  </div>
                  <div className="group-info">
                    <div className="group-info-header">
                      <h4 className="group-name">{getGroupName(g)}</h4>
                    </div>
                  </div>
                  <div style={{ position: 'relative' }}>
                    <button className="btn-group-menu" onClick={(e) => { e.stopPropagation(); setActiveGroupMenuId(activeGroupMenuId === g._id ? null : g._id); }}>⋮</button>
                    {activeGroupMenuId === g._id && (
                      <div className="group-dropdown-menu" style={{ position: 'absolute', right: 0, top: '25px', background: 'white', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.1)', zIndex: 10, width: '120px' }}>
                        <button style={{ width: '100%', padding: '8px 10px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }} onClick={(e) => toggleMuteNotifications(e, g)}>
                          {g.isMutedByMe ? 'Unmute chat' : 'Mute chat'}
                        </button>
                        <button style={{ width: '100%', padding: '8px 10px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px' }} onClick={(e) => clearChat(e, g._id)}>Clear chat</button>
                        <button style={{ width: '100%', padding: '8px 10px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '12px', color: 'red' }} onClick={(e) => deleteGroup(e, g._id)}>Delete group</button>
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>

          </div>
        </div>

        {/* Messaging Area */}
        {activeGroup ? (
          <div className="messaging-area">
            <div className="messaging-header" style={{ display: 'flex', justifyContent: 'space-between', borderBottom: '1px solid #f0f0f0', paddingBottom: '10px', paddingTop: '10px', alignItems: 'center' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <div className="group-avatar header-avatar" style={{ width: '40px', height: '40px', overflow: 'hidden', borderRadius: '50%', background: '#e0e4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', cursor: 'pointer' }} onClick={() => setShowAvatarModal(true)}>
                  {renderAvatar(getGroupName(activeGroup), getGroupAvatarUrl(activeGroup), activeGroup._id, '16px')}
                </div>
                <div>
                  <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '700', color: 'var(--chat-primary)' }}>{getGroupName(activeGroup)}</h3>
                </div>
              </div>
              
              <div className="header-actions" style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                {!activeGroup.isDirectMessage && (
                  <div style={{ position: 'relative' }}>
                    <button className="btn-header-actions" style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'white', border: '1px solid #ccc', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '14px', fontWeight: '500' }} onClick={(e) => { e.stopPropagation(); setActiveGroupActionsId(activeGroupActionsId === 'header' ? null : 'header'); }}>
                      <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="9" cy="7" r="4"></circle><path d="M23 21v-2a4 4 0 0 0-3-3.87"></path><path d="M16 3.13a4 4 0 0 1 0 7.75"></path></svg>
                      {activeGroup.members?.length || 0}
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9"></polyline></svg>
                    </button>
                    {activeGroupActionsId === 'header' && (
                      <div className="header-dropdown-menu" style={{ position: 'absolute', right: 0, top: '40px', background: 'white', border: '1px solid #e0e0e0', borderRadius: '8px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)', zIndex: 20, width: '220px', overflow: 'hidden' }}>
                        <div style={{ padding: '10px 15px', borderBottom: '1px solid #f0f0f0', fontSize: '12px', color: '#666', fontWeight: '600' }}>People ({activeGroup.members?.length || 0})</div>
                        <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                          {activeGroup.members?.map(m => (
                            <div key={m._id || m.phoneNumber} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 15px', borderBottom: '1px solid #f8f8f8' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                                <div style={{ width: '24px', height: '24px', borderRadius: '50%', fontSize: '10px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden' }}>
                                  {renderAvatar(m.fullName || m.phoneNumber, m.userImage, m._id || m.id)}
                                </div>
                                <span style={{ fontSize: '13px' }}>{m.fullName || m.name || m.phoneNumber}</span>
                              </div>
                              <button style={{ background: 'none', border: 'none', color: '#999', cursor: 'pointer', fontSize: '14px' }} onClick={() => removeGroupMember(activeGroup._id, m.phoneNumber)}>×</button>
                            </div>
                          ))}
                        </div>
                        <button className="btn-add-people" onClick={() => { 
                          setSelectedMembersToAdd(activeGroup.members?.map(m => {
                            const phone = m.phoneNumber || usersList.find(u => (u._id || u.id) === (m._id || m.id))?.phoneNumber;
                            return String(phone || '');
                          }).filter(Boolean) || []);
                          setShowAddMemberModal(true); 
                        }}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path><circle cx="8.5" cy="7" r="4"></circle><line x1="20" y1="8" x2="20" y2="14"></line><line x1="23" y1="11" x2="17" y2="11"></line></svg>
                          Add people
                        </button>
                        <button className="btn-leave-group" onClick={(e) => leaveGroup(e, activeGroup._id)}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path><polyline points="16 17 21 12 16 7"></polyline><line x1="21" y1="12" x2="9" y2="12"></line></svg>
                          Leave
                        </button>
                      </div>
                    )}
                  </div>
                )}
                {!activeGroup.isDirectMessage && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', background: 'white', border: '1px solid #ccc', borderRadius: '6px', padding: '6px 12px' }}>
                    <input type="checkbox" id="header-audio-call" checked={activeGroup.isAudioCallAllowed} onChange={e => updateCallPermissions(activeGroup._id, e.target.checked)} style={{ cursor: 'pointer', margin: 0, accentColor: '#1e88e5' }} />
                    <label htmlFor="header-audio-call" style={{ fontSize: '13px', cursor: 'pointer', userSelect: 'none', margin: 0, paddingLeft: '4px', fontWeight: '500', color: '#333' }}>Allow Audio call</label>
                  </div>
                )}
                <button className="call-btn" onClick={() => initiateCall('audio')} title="Audio Call" style={{ background: '#f0f4f8', border: 'none', borderRadius: '6px', padding: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', width: '34px', height: '34px' }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M22 16.92V19.92C22 20.4723 21.5523 20.92 21 20.92C10.5066 20.92 2 12.4134 2 1.92C2 1.36772 2.44772 0.919998 3 0.919998H6C6.55228 0.919998 7 1.36772 7 1.92C7 2.87974 7.15579 3.82283 7.45607 4.7171C7.62589 5.22271 7.49132 5.78768 7.10629 6.17272L5.43851 7.84049C7.45892 11.5996 10.4004 14.5411 14.1595 16.5615L15.8273 14.8937C16.2123 14.5087 16.7773 14.3741 17.2829 14.5439C18.1772 14.8442 19.1203 15 20.08 15C20.6323 15 21.08 15.4477 21.08 16V16.92Z" fill="#1e3a8a" stroke="#1e3a8a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            </div>

            <div className="messages-container" style={{ padding: '20px', background: '#fdfdfd' }}>
              {messages.map(msg => {
                if (msg.isSystemMessage) {
                  return (
                    <div key={msg._id} style={{ display: 'flex', justifyContent: 'center', margin: '15px 0' }}>
                      <div style={{ background: '#e8ecf1', padding: '6px 15px', borderRadius: '20px', fontSize: '12px', color: '#666', display: 'flex', alignItems: 'center', gap: '6px' }}>
                        {msg.content}
                      </div>
                    </div>
                  );
                }
                const isMe = msg.sender._id === user.id;
                const msgSenderAvatarUrl = msg.sender?.avatarUrl || msg.sender?.userImage || msg.sender?.avatar || null;
                return (
                  <div key={msg._id} style={{ display: 'flex', justifyContent: isMe ? 'flex-end' : 'flex-start', margin: '15px 0' }}>
                    <div style={{ display: 'flex', flexDirection: isMe ? 'row-reverse' : 'row', alignItems: 'flex-end', gap: '10px' }}>
                      <div style={{ width: '32px', height: '32px', borderRadius: '50%', background: '#e0e4ec', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '14px', fontWeight: 'bold', overflow: 'hidden' }}>
                        {renderAvatar(msg.sender?.name === 'User' ? msg.sender?.phoneNumber : (msg.sender?.name || msg.sender?.phoneNumber), msgSenderAvatarUrl, msg.sender?._id, '14px')}
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: isMe ? 'flex-end' : 'flex-start' }}>
                        <div style={{ fontSize: '10px', color: '#999', marginBottom: '4px' }}>
                          {isMe ? 'You' : (msg.sender?.name === 'User' ? msg.sender?.phoneNumber : (msg.sender?.name || msg.sender?.phoneNumber))}
                        </div>
                        <div style={{ background: isMe ? '#e6f3ff' : '#f0f2f5', color: '#333', padding: '10px 15px', borderRadius: '15px', borderBottomRightRadius: isMe ? '0' : '15px', borderBottomLeftRadius: isMe ? '15px' : '0', fontSize: '13px' }}>
                          {msg.content}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            <div className="message-input-container" style={{ padding: '15px 20px', background: 'white', borderTop: 'none', position: 'relative' }}>
              {showEmojiPicker && (
                <div className="emoji-picker-menu" style={{ position: 'absolute', bottom: '70px', right: '30px', background: 'white', border: '1px solid #ccc', borderRadius: '8px', padding: '10px', width: '250px', height: '200px', overflowY: 'auto', display: 'flex', flexWrap: 'wrap', gap: '5px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
                  {emojiList.map((em, idx) => (
                    <span key={idx} style={{ cursor: 'pointer', fontSize: '20px' }} onClick={() => handleEmojiSelect(em)}>{em}</span>
                  ))}
                </div>
              )}
              <form onSubmit={handleSendMessage} style={{ display: 'flex', alignItems: 'center', border: '1px solid #ddd', borderRadius: '8px', padding: '5px 10px' }}>
                <input 
                  type="text" 
                  style={{ flex: 1, border: 'none', outline: 'none', padding: '10px', fontSize: '14px', color: '#333' }}
                  placeholder="Type a message..." 
                  value={newMessage}
                  onChange={e => setNewMessage(e.target.value)}
                />
                <div style={{ display: 'flex', gap: '15px', alignItems: 'center' }}>
                  <span className="btn-emoji-picker" style={{ color: '#888', cursor: 'pointer', display: 'flex', alignItems: 'center' }} onClick={() => setShowEmojiPicker(!showEmojiPicker)}>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="10"></circle><path d="M8 14s1.5 2 4 2 4-2 4-2"></path><line x1="9" y1="9" x2="9.01" y2="9"></line><line x1="15" y1="9" x2="15.01" y2="9"></line></svg>
                  </span>
                  <button type="submit" style={{ background: '#0D1A3A', border: 'none', color: 'white', width: '36px', height: '36px', borderRadius: '8px', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }} disabled={!newMessage.trim()}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="22" y1="2" x2="11" y2="13"></line><polygon points="22 2 15 22 11 13 2 9 22 2"></polygon></svg>
                  </button>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className="messaging-area" style={{ alignItems: 'center', justifyContent: 'center', background: '#fdfdfd' }}>
            {/* Empty State mapping not needed if exact match, left empty is fine, or simple text */}
          </div>
        )}
      </div>

      {/* New Chat Modal */}
      {showNewChatModal && (
        <div className="modal-overlay" onClick={() => setShowNewChatModal(false)}>
          <div className="modal-content new-chat-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Start a Chat</h2>
              <button className="close-btn" onClick={() => setShowNewChatModal(false)}>×</button>
            </div>
            <div className="modal-search">
              <input 
                type="text" 
                className="search-input" 
                placeholder="Search residents..." 
                value={modalSearchQuery}
                onChange={e => setModalSearchQuery(e.target.value)}
              />
            </div>
            <div className="resident-list">
              {usersList.filter(u => (u.name || u.fullName || '').toLowerCase().includes(modalSearchQuery.toLowerCase()) || u.phoneNumber?.includes(modalSearchQuery)).map(u => (
                <div key={u._id || u.id} className="resident-item" onClick={() => startDirectMessage(u.id || u._id)}>
                  <div className="resident-avatar" style={{ width: '40px', height: '40px', overflow: 'hidden', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#e0e4ec', borderRadius: '50%', color: '#0D1A3A', fontWeight: 'bold' }}>
                    {renderAvatar(u.name || u.fullName || u.phoneNumber, u.userImage, u._id || u.id, '16px')}
                  </div>
                  <div className="resident-info">
                    <h4>{u.name || u.fullName || 'User'}</h4>
                    <p>{u.role?.name || u.role || 'Resident'} • {u.phoneNumber}</p>
                  </div>
                  <span className="action-arrow">→</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* New Group Modal */}
      {showNewGroupModal && (
        <div className="modal-overlay" onClick={() => setShowNewGroupModal(false)}>
          <div className="modal-content new-group-modal" onClick={e => e.stopPropagation()}>
            <button className="absolute-close-btn" onClick={() => setShowNewGroupModal(false)}>×</button>

            <div className="group-left-panel">
              <h2>Create New Group</h2>
              
              <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', marginBottom: '6px', color: '#333' }}>
                Group Name <span style={{ color: 'red' }}>*</span>
              </label>
              <input 
                type="text" 
                className="group-name-input"
                placeholder="Enter group name" 
                value={newGroupName}
                onChange={e => setNewGroupName(e.target.value)}
              />

              <div className="hierarchy-section">
                <h3>Location Hierarchy</h3>
                <div className="hierarchy-tree">
                  <div className="tree-node">
                    <label className="node-header">
                      <input type="checkbox" />
                      <span className="node-label" style={{ fontWeight: 'bold' }}>Sites</span>
                    </label>
                    <div className="node-children">
                      <div className="tree-node">
                        <label className="node-header">
                          <input type="checkbox" />
                          <span className="node-label">Site 1</span>
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div className="target-users-count">Target Users: 1</div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '15px' }}>
                <input type="checkbox" id="allow-audio-group" checked={isGroupCallAllowed} onChange={e => setIsGroupCallAllowed(e.target.checked)} />
                <label htmlFor="allow-audio-group" style={{ fontSize: '12px' }}>Allow Audio call</label>
              </div>

              <button className="btn-create-group" onClick={createGroup} disabled={!newGroupName.trim()}>
                Create Group
              </button>
            </div>

            <div className="group-right-panel">
              <h2>Existing Groups</h2>
              
              {groups.filter(g => !g.isDirectMessage).map(group => (
                <div key={group._id} className="existing-group-card">
                  <div className="group-card-header">
                    <h3>{group.name}</h3>
                    <button className="btn-delete-group" onClick={(e) => deleteGroup(e, group._id)}>Delete Group</button>
                  </div>

                  <div style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', gap: '6px' }}>
                    <input type="checkbox" id={`audio-${group._id}`} checked={group.isAudioCallAllowed} onChange={e => updateCallPermissions(group._id, e.target.checked)} />
                    <label htmlFor={`audio-${group._id}`} style={{ fontSize: '12px' }}>Allow Audio call</label>
                  </div>

                  <div className="members-info-row" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '5px' }}>
                    <p className="members-count" style={{ color: '#888', fontSize: '9px', margin: 0 }}>Members: {group.members?.length || 0}</p>
                    <button className="btn-view-members" onClick={() => setActiveGroupActionsId(activeGroupActionsId === group._id ? null : group._id)} style={{ color: '#1e3a8a', fontWeight: '600', fontSize: '9px', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>
                      {activeGroupActionsId === group._id ? 'Hide Members' : 'View Members'}
                    </button>
                  </div>
                  
                  {activeGroupActionsId === group._id && (
                    <div style={{ marginTop: '10px', marginBottom: '15px' }}>
                      {group.members?.map(m => (
                        <div key={m._id || m.phoneNumber} className="member-pill">
                          <span>{m.fullName || m.name || m.phoneNumber}</span>
                          <button onClick={() => removeGroupMember(group._id, m.phoneNumber)}>×</button>
                        </div>
                      ))}
                    </div>
                  )}

                  <div className="add-member-row">
                    <select className="input-field" id={`add-member-${group._id}`} defaultValue="">
                      <option value="" disabled>Select user to add...</option>
                      {usersList.map(u => (
                        <option key={u.id || u._id} value={u.phoneNumber}>{u.fullName || u.phoneNumber}</option>
                      ))}
                    </select>
                    <button className="btn-primary" onClick={() => {
                      const select = document.getElementById(`add-member-${group._id}`);
                      addGroupMember(group._id, select.value);
                      select.value = '';
                    }}>Add</button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Calling Overlays */}
      {callConfig?.active && (
        <CallDialog 
          groupId={callConfig.groupId}
          isVideo={callConfig.type === 'video'}
          onClose={handleEndCall}
          members={activeGroup?.members || []}
          isInitiator={callConfig.initiator}
          callAnswered={callAnswered}
          isDirectMessage={activeGroup?.isDirectMessage}
        />
      )}

      {incomingCallData && !callConfig?.active && (
         <IncomingCall 
            incomingData={incomingCallData}
            onAccept={() => {
               socket.emit('answer-call-ring', { groupId: incomingCallData.groupId });
               const g = groups.find(x => x._id === incomingCallData.groupId);
               if (g) setActiveGroup(g);
               setCallConfig({ active: true, type: incomingCallData.isVideo ? 'video' : 'audio', initiator: false, groupId: incomingCallData.groupId });
               setIncomingCallData(null);
            }}
            onDecline={() => {
               socket.emit('decline-call-ring', { groupId: incomingCallData.groupId });
               setIncomingCallData(null);
            }}
         />
      )}

      {/* Avatar Modal */}
      {showAvatarModal && activeGroup && (
        <>
          <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', zIndex: 9998 }} onClick={() => setShowAvatarModal(false)}></div>
          <div style={{ position: 'absolute', top: '50%', left: '330px', transform: 'translateY(-50%)', width: '160px', height: '160px', background: 'rgba(255, 255, 255, 0.15)', backdropFilter: 'blur(8px)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999, boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
            <div style={{ width: '130px', height: '130px', borderRadius: '50%', overflow: 'hidden', boxShadow: '0 10px 25px rgba(0,0,0,0.3)', background: '#e0e4ec', border: '3px solid white', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              {renderAvatar(getGroupName(activeGroup), getGroupAvatarUrl(activeGroup), activeGroup._id, '48px')}
            </div>
          </div>
        </>
      )}

      {/* Add Member Modal */}
      {showAddMemberModal && (
        <div className="modal-overlay" onClick={() => { setShowAddMemberModal(false); setSelectedMembersToAdd([]); setAddMemberSearchQuery(''); }}>
          <div className="modal-content new-chat-modal" onClick={e => e.stopPropagation()} style={{ padding: 0, width: '450px', display: 'flex', flexDirection: 'column' }}>
            <div className="modal-header" style={{ padding: '20px', borderBottom: '1px solid #eee' }}>
              <h2 style={{ fontSize: '16px', margin: 0, fontWeight: '700', color: '#333' }}>Add Member to Group</h2>
              <button className="close-btn" onClick={() => { setShowAddMemberModal(false); setSelectedMembersToAdd([]); setAddMemberSearchQuery(''); }}>×</button>
            </div>
            
            <div style={{ padding: '15px 20px', borderBottom: '1px solid #eee' }}>
              <input 
                type="text" 
                style={{ width: '100%', padding: '8px 12px', border: '1px solid #e0e0e0', borderRadius: '6px', fontSize: '13px', outline: 'none' }}
                placeholder="Search residents..." 
                value={addMemberSearchQuery}
                onChange={e => setAddMemberSearchQuery(e.target.value)}
              />
            </div>
            
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 20px', background: '#f8f9fa', borderBottom: '1px solid #eee' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: '10px', fontSize: '14px', fontWeight: '500', color: '#374151', cursor: 'pointer', margin: 0 }}>
                <input 
                  type="checkbox" 
                  style={{ width: '14px', height: '14px', accentColor: '#4f6bed', cursor: 'pointer' }} 
                  checked={usersList.length > 0 && selectedMembersToAdd.length === usersList.length}
                  onChange={(e) => {
                    if (e.target.checked) setSelectedMembersToAdd(usersList.map(u => String(u.phoneNumber)));
                    else setSelectedMembersToAdd([]);
                  }}
                />
                Select All
              </label>
              <span style={{ fontSize: '13px', color: '#6b7280' }}>{selectedMembersToAdd.length} selected</span>
            </div>

            <div className="resident-list multi-select-mode" style={{ maxHeight: '300px', overflowY: 'auto' }}>
              {usersList.filter(u => (u.name || u.fullName || u.phoneNumber || '').toString().toLowerCase().includes(addMemberSearchQuery.toLowerCase())).map(u => {
                const isSelected = selectedMembersToAdd.includes(String(u.phoneNumber));
                return (
                  <div key={u.id || u._id} className="resident-item" style={{ padding: '12px 20px', borderBottom: '1px solid #f0f0f0', display: 'flex', alignItems: 'center', cursor: 'pointer', background: isSelected ? 'rgba(79, 107, 237, 0.05)' : 'white' }} onClick={() => {
                    if (isSelected) setSelectedMembersToAdd(selectedMembersToAdd.filter(p => p !== String(u.phoneNumber)));
                    else setSelectedMembersToAdd([...selectedMembersToAdd, String(u.phoneNumber)]);
                  }}>
                    <input 
                      type="checkbox" 
                      style={{ width: '14px', height: '14px', accentColor: '#4f6bed', cursor: 'pointer', marginRight: '15px' }} 
                      checked={isSelected} 
                      readOnly
                    />
                    <div style={{ width: '36px', height: '36px', borderRadius: '50%', background: '#e0e4ec', color: '#0D1A3A', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', marginRight: '15px' }}>
                      {(u.name || u.fullName || u.phoneNumber || 'U').charAt(0).toUpperCase()}
                    </div>
                    <div style={{ flex: 1 }}>
                      <h4 style={{ margin: '0 0 2px 0', fontSize: '13px', color: '#333' }}>{u.name || u.fullName || u.phoneNumber}</h4>
                      <p style={{ margin: 0, fontSize: '11px', color: '#888' }}>{u.role?.name || u.role || 'Resident'} • {u.phoneNumber}</p>
                    </div>
                  </div>
                );
              })}
            </div>

            <div style={{ padding: '15px 20px', borderTop: '1px solid #eee', display: 'flex', justifyContent: 'space-between', gap: '10px' }}>
              <button style={{ flex: 1, padding: '8px', background: 'transparent', border: '1px solid #d1d1d1', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: '#333', cursor: 'pointer' }} onClick={() => { setShowAddMemberModal(false); setSelectedMembersToAdd([]); setAddMemberSearchQuery(''); }}>Cancel</button>
              <button 
                style={{ flex: 1, padding: '8px', background: '#4f6bed', border: 'none', borderRadius: '6px', fontSize: '13px', fontWeight: '600', color: 'white', cursor: selectedMembersToAdd.length > 0 ? 'pointer' : 'not-allowed', opacity: selectedMembersToAdd.length > 0 ? 1 : 0.6 }}
                disabled={selectedMembersToAdd.length === 0}
                onClick={async () => {
                   // Calculate additions
                   const newlyAdded = selectedMembersToAdd.filter(phone => !activeGroup.members?.some(m => {
                     const mPhone = m.phoneNumber || usersList.find(u => (u._id || u.id) === (m._id || m.id))?.phoneNumber;
                     return String(mPhone || '') === phone;
                   }));
                   if (newlyAdded.length > 0) {
                     await addGroupMember(activeGroup._id, newlyAdded);
                   }
                   
                   // Calculate removals
                   const removedMembers = activeGroup.members?.filter(m => {
                     const mPhone = m.phoneNumber || usersList.find(u => (u._id || u.id) === (m._id || m.id))?.phoneNumber;
                     return !selectedMembersToAdd.includes(String(mPhone || ''));
                   }) || [];
                   for (const rm of removedMembers) {
                     const rmPhone = rm.phoneNumber || usersList.find(u => (u._id || u.id) === (rm._id || rm.id))?.phoneNumber;
                     if (rmPhone) await removeGroupMember(activeGroup._id, String(rmPhone));
                   }

                   setShowAddMemberModal(false);
                   setSelectedMembersToAdd([]);
                   setAddMemberSearchQuery('');
                }}
              >
                Save Changes ({selectedMembersToAdd.length})
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      {showConfirmModal.visible && (
        <div className="confirm-modal-overlay" onClick={() => setShowConfirmModal({ visible: false, type: '', groupId: null, groupName: '' })}>
          <div className="confirm-modal-card" onClick={e => e.stopPropagation()}>
            <div className="confirm-icon-zone icon-zone--info">
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                <polyline points="16 17 21 12 16 7"></polyline>
                <line x1="21" y1="12" x2="9" y2="12"></line>
              </svg>
            </div>
            
            <h2 className="confirm-title">Leave Group</h2>
            
            <div className="confirm-group-badge">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"></path>
              </svg>
              <span>{showConfirmModal.groupName}</span>
            </div>

            <p className="confirm-description">
              You will leave this group and no longer receive its messages. You can be re-added by an admin.
            </p>

            <div className="confirm-divider"></div>

            <div className="confirm-actions">
              <button className="confirm-btn-cancel" onClick={() => setShowConfirmModal({ visible: false, type: '', groupId: null, groupName: '' })}>Cancel</button>
              <button className="confirm-btn-confirm" onClick={() => {
                 leaveGroup({ preventDefault: () => {} }, showConfirmModal.groupId);
                 setShowConfirmModal({ visible: false, type: '', groupId: null, groupName: '' });
                 setActiveGroup(null);
              }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                Leave Group
              </button>
            </div>
          </div>
        </div>
      )}

    </div>
    </div>
  );
};

export default ChatApp;
